import { Button, message, Select, Tag, Tooltip } from "antd";
import _ from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiRequest } from "../../api";
import apiPaths from "../../api/paths";
import { getMoreData } from "../../common";
import DynamicTable from "../../components/DynamicTable";
import EndRideModal from "./components/EndRideModal";
import SelectStationModal from "./components/SelectStationModal";
import DateRangeSelect from "../../components/DateRangeSelect";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import download from "downloadjs";

function Rides() {
  const [rides, setRides] = useState([]);
  const [stations, setStations] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [selectedRange, setSelectedRange] = useState([
    moment().subtract(30, "days"),
    moment(),
  ]);
  const [showEndRide, setShowEndRide] = useState(null);
  const [showSelectStation, setShowSelectStation] = useState(null);
  const [type, setType] = useState("buggy");
  const location = useLocation();
  const { filteredValues = {} } = location.state || {};
  const showStartEnd = ["bike", "Barc"].includes(type);

  const DEFAULT_COLUMNS = {
    id: {
      show: true,
      label: "Ride Id",
      key: "id",
    },
    user: {
      show: true,
      label: "User",
      key: "user",
    },
    campus: {
      show: true,
      label: "Campus",
      key: "campus",
    },
    start_dock: {
      show: showStartEnd,
      label: "Start Dock",
      key: "start_dock",
    },
    end_dock: {
      show: showStartEnd,
      label: "End Dock",
      key: "start_dock",
    },
    feedback: {
      show: showStartEnd,
      label: "Feedback",
      key: "feedback",
    },
    vehicle: {
      show: true,
      label: "Vehicle",
      key: "vehicle",
    },
    charges: {
      show: true,
      label: "Charges",
      key: "charges",
    },
    type: {
      show: true,
      label: "Type",
      key: "type",
    },
    start_time: {
      show: true,
      label: "Ride Date",
      key: "start_time",
    },
  };

  useEffect(() => {
    fetchStations();
    fetchCampuses();
  }, []);

  useEffect(() => {
    fetchRides(type, selectedRange);
  }, [type, selectedRange]);

  const fetchStations = async () => {
    const res = await apiRequest({
      path: apiPaths.stations,
    });
    if (!res.error) {
      setStations(res.results);
    }
  };

  const fetchCampuses = async () => {
    const res = await apiRequest({
      path: apiPaths.campuses,
    });
    if (!res.error) {
      let options = res.results.map((item) => ({
        text: item.name,
        value: item.code,
      }));
      setCampuses(options);
    }
  };

  const fetchRides = async (type, dates) => {
    let startDate = dates[0];
    let endDate = dates[1];
    const res = await apiRequest({
      path:
        apiPaths.rides +
        `?type=${type}&start_date=${startDate}&end_date=${endDate}`,
    });
    if (!res.error) {
      if (res.next) {
        return new Promise((resolve, reject) => {
          getMoreData({
            path: res.next,
            data: res.results,
            onFinish: (data) => {
              setRides(data);
              resolve({ results: data });
            },
          });
        });
      } else {
        setRides(res.results);
      }
    } else {
      message.error(res.error_description);
    }
  };

  const handleRideUpdate = (res) => {
    let rideIndex = rides.findIndex((item) => res.id == item.id);
    let newRides = [...rides];
    newRides[rideIndex] = {
      ...newRides[rideIndex],
      ...res,
    };
    setRides(newRides);
  };

  const COLUMNS = [
    {
      title: "Ride Id",
      dataIndex: "id",
      fixed: "left",
      width: 100,
      filterType: "search",
    },
    {
      title: "User",
      dataIndex: "user.user_email",
      width: 140,
      filterType: "search",
    },
    {
      title: "Campus",
      dataIndex: "campus",
      width: 140,
      filterType: "filter",
      filters: campuses,
      onFilter: (value, record) =>
        record.start_dock?.campus?.code == value ||
        record.user?.campus_info?.name == value,
      render: (_, record) => {
        return (
          record.start_dock?.campus?.name || record.user?.campus_info?.name
        );
      },
    },
    {
      title: "Start Dock",
      dataIndex: "start_dock.name",
      width: 140,
      filterType: "search",
    },
    {
      title: "End Dock",
      dataIndex: "end_dock.name",
      width: 140,
      filterType: "search",
    },
    {
      title: "Vehicle",
      dataIndex: "vehicle.code",
      width: 140,
      filterType: "search",
      render: (_, row) => (
        <>
          <div>{row.vehicle?.name}</div>
          <div style={{ fontSize: 10 }}>{row.vehicle?.code}</div>
        </>
      ),
    },
    {
      title: "Charges",
      dataIndex: "charges",
      width: "90px",
      render: (cost) => {
        return <div>{cost}</div>;
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      width: "90px",
      filters: [
        {
          text: "With Plan",
          value: "ride_with_plan",
        },
        {
          text: "With Wallet",
          value: "ride",
        },
        {
          text: "With Upi",
          value: "ride_with_upi",
        },
      ],
      onFilter: (value, record) => {
        if (value == "ride") {
          return record.type == value || record.type == undefined;
        }
        return record.type == value;
      },
      render: (type) => {
        switch (type) {
          case "ride_with_plan": {
            return <Tag color={"orange"}>{"With Plan"}</Tag>;
          }
          case "ride_with_upi": {
            return <Tag color={"purple"}>{"With UPI"}</Tag>;
          }
          default:
            return <Tag color={"green"}>{"With Wallet"}</Tag>;
        }
      },
    },
    {
      title: showStartEnd ? "Start Time" : "Ride Date",
      dataIndex: "start_time",
      width: "150px",
      filterType: "date",
      defaultFilteredValue: filteredValues.start_time,
      render: (value) => {
        return <div>{moment(value).format("DD-MM-YYYY HH:mm")}</div>;
      },
    },
    ...(showStartEnd
      ? [
          {
            title: "End Time",
            dataIndex: "end_time",
            width: "150px",
            filterType: "date",
            defaultFilteredValue: filteredValues.end_time,
            render: (value) => {
              return <div>{moment(value).format("DD-MM-YYYY HH:mm")}</div>;
            },
          },
          {
            title: "Status",
            dataIndex: "status",
            width: "100px",
            filterType: "search",
            render: (value) => {
              return (
                <div
                  style={{
                    color:
                      value == "ended"
                        ? "green"
                        : value == "cancelled"
                        ? "red"
                        : "orange",
                  }}
                >
                  {value}
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: showStartEnd ? "Reason" : "Feedback",
      dataIndex: "feedback",
      width: "190px",
      filterType: "search",
      render: (value) => {
        return <div>{value}</div>;
      },
    },
  ];

  let EXPORT_COLUMNS = {
    "Ride Id": (data) => data.id,
    Vehicle: (data) => `${data.vehicle?.name} (${data.vehicle?.code})`,
    User: (data) => data.user.user_email,
    Campus: (data) =>
      data.start_dock?.campus?.name || data.user?.campus_info?.name,
    Charge: (data) => data.charges,
    Type: (data) => data.type,
    Status: (data) => data.status,
    Date: (data) => moment(data.start_time).format("DD-MM-YYYY hh:mm a"),
    "Reason/ Feedback": (data) => data.feedback,
  };

  const downLoadFile = async (items) => {
    let itemsRef = items.slice(0);

    let jsonData = itemsRef.map((item) => {
      let formatedItem = Object.keys(EXPORT_COLUMNS).reduce((obj, key) => {
        obj[key] = EXPORT_COLUMNS[key](item);
        return obj;
      }, {});
      return formatedItem;
    });
    let workbook = XLSX.utils.book_new();
    let worksheet = XLSX.utils.json_to_sheet(jsonData);
    var range = XLSX.utils.decode_range(worksheet["!ref"]);
    worksheet["!cols"] = [...Array(range.e.c)].map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(workbook, worksheet, "sheet_1");
    let xlBuffer = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
    let fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;";
    const data = new Blob([xlBuffer], { type: fileType });
    download(data, `rides-${Date.now()}.xlsx`, fileType);
  };

  return (
    <div style={{ height: "80vh" }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <div>
          {"Showing: "}
          <Select
            value={type}
            style={{ width: 160 }}
            onChange={(value) => {
              setType(value);
            }}
          >
            <Select.Option key={"buggy"}>Buggy Rides</Select.Option>
            <Select.Option key={"bike"}>Bike Rides</Select.Option>
            <Select.Option key={"Barc"}>Barc Rides</Select.Option>
          </Select>
        </div>
        <div style={{ marginRight: 10 }}>
          <DateRangeSelect
            selectedRange={selectedRange}
            onSelectedRangeChanged={setSelectedRange}
          />
          <Tooltip title="Download Rides">
            <Button
              icon={<DownloadOutlined />}
              style={{ marginLeft: 15 }}
              onClick={() => {
                downLoadFile(rides);
              }}
              disabled={!rides?.length}
            />
          </Tooltip>
        </div>
      </div>
      <div style={{ height: "90%", marginTop: 30 }}>
        <DynamicTable
          dataSource={rides}
          columns={COLUMNS}
          defaultColumns={DEFAULT_COLUMNS}
          scroll={{ y: "65vh" }}
          pagination={{
            pageSize: 200,
          }}
          showAction={() => false}
          renderAction={(record) => {
            if (record.status == "cancelled" || type !== "bike") {
              return null;
            }
            return (
              <>
                <Button
                  onClick={() => {
                    setShowEndRide({
                      ...record,
                    });
                  }}
                  size="small"
                >
                  Cancel Ride
                </Button>
                {/* <br />
                  <Button
                    onClick={() => {
                      setShowEndRide(record);
                    }}
                    type="primary"
                    size="small"
                    style={{ marginTop: 10 }}
                  >
                    End Ride
                  </Button> */}
              </>
            );
          }}
        />
      </div>
      {showEndRide ? (
        <EndRideModal
          rideId={showEndRide.id}
          isCancel={true}
          hideStation={showEndRide.status == "ended"}
          rideData={showEndRide}
          onUpdated={(res) => {
            handleRideUpdate(res);
            setShowEndRide(null);
          }}
          stations={stations}
          onCancel={() => {
            setShowEndRide(null);
          }}
          open={true}
        />
      ) : null}
      {showSelectStation ? (
        <SelectStationModal
          rideId={showSelectStation.rideId}
          isStart={showSelectStation.isStart}
          onUpdated={(res) => {
            handleRideUpdate(res);
            setShowSelectStation(null);
          }}
          stations={stations}
          onCancel={() => {
            setShowSelectStation(null);
          }}
          open={true}
        />
      ) : null}
    </div>
  );
}

export default Rides;
